/**
 * Runs migration v2 commands against the simulated world: the same scripts, flags, order of
 * phases and console messages as tools/migration-v2, with the legacy MySQL source replaced by
 * synthetic employees (source.ts) and HRMS Postgres by a pretend store (state.ts → HrmsDb).
 */
import {
  arg, describeFilters, filtersFromArgs, hasSelectionFlags, selectEmployees, selectionLines, validateFilters,
  type SelectionFilters,
} from "./selection.js";
import { generateSource, HOLIDAY_ROWS, type SimEmployee } from "./source.js";
import {
  emptyHrms, findRun, finishRun, historyLines, migratedBayIds, progress, progressLines, resetState, startRun,
  STATE_FILE, updateSource, type ModuleSummary, type SimWorld, type VerifySummary,
} from "./state.js";

export type LineTone = "plain" | "muted" | "head" | "info" | "success" | "warn" | "error" | "cmd";

export interface SimIo {
  print(text: string, tone?: LineTone): void;
  /** A long phase's progress bar; null hides it. */
  progress(p: { label: string; percent: number } | null): void;
  sleep(ms: number): Promise<void>;
  confirm(q: { title: string; description: string; actionText: string; destructive?: boolean }): Promise<boolean>;
  /** Interactive mode: the filter panel is the picker, so its current filters are the answers. */
  panelFilters(): SelectionFilters;
  /** The tool's saveState(): persist and redraw the dashboard mid-command. */
  commit(): void;
}

export const PACKAGE = "@hrms/migration-v2";

export const SCRIPTS: Record<string, { file: string; about: string }> = {
  "migrate:team":    { file: "src/run-team.ts",              about: "pick employees (filters or interactive) and load them" },
  "preview":         { file: "src/run-team.ts --preview",    about: "show who the filters pick, no writes" },
  "migrate:modules": { file: "src/run-modules.ts",           about: "personal, termination, leave, attendance, holiday, compensation" },
  "verify":          { file: "src/verify-modules.ts",        about: "reconcile old vs new for leave and attendance" },
  "migrate:all":     { file: "src/run-all.ts",               about: "migrate:team → migrate:modules → verify → status" },
  "status":          { file: "src/progress.ts status",       about: "progress %, by status and department" },
  "history":         { file: "src/progress.ts history",      about: "every run: filters, counts, modules, verify" },
  "show":            { file: "src/progress.ts show",         about: "the people one run picked, and its failures" },
  "reset":           { file: "src/progress.ts reset",        about: "forget all runs and start from 0% (HRMS data stays)" },
  "reset:db":        { file: "src/reset.ts",                 about: "TRUNCATE employees, users, departments, offices" },
  "docs:organize":   { file: "src/organize-legacy-docs.ts",  about: "sort the legacy document folder (not simulated)" },
  "docs:import":     { file: "src/import-legacy-docs.ts",    about: "load legacy documents for migrated employees" },
};

class Fatal extends Error {}

// Splits a command line like a shell: spaces separate, quotes group.
export function tokenize(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let quote: string | null = null;
  let has = false;
  for (const ch of line) {
    if (quote) {
      if (ch === quote) quote = null; else cur += ch;
    } else if (ch === '"' || ch === "'") { quote = ch; has = true; }
    else if (/\s/.test(ch)) { if (has || cur) out.push(cur); cur = ""; has = false; }
    else { cur += ch; has = true; }
  }
  if (has || cur) out.push(cur);
  return out;
}

/** `pnpm --filter @hrms/migration-v2 [run] <script> …flags` or just `<script> …flags`. */
export function parseCommand(line: string): { script: string; args: string[] } {
  let t = tokenize(line.trim());
  if (t[0] === "pnpm") {
    t = t.slice(1);
    const fi = t.findIndex((a) => a === "--filter" || a === "-F" || a.startsWith("--filter="));
    if (fi >= 0) {
      const pkg = t[fi]!.startsWith("--filter=") ? t[fi]!.slice(9) : t[fi + 1];
      if (pkg && pkg !== PACKAGE) throw new Fatal(`Only ${PACKAGE} is simulated here (got ${pkg}).`);
      t.splice(fi, t[fi]!.startsWith("--filter=") ? 1 : 2);
    }
    if (t[0] === "run") t = t.slice(1);
  }
  const [script = "", ...rest] = t;
  // pnpm 9 hands a literal `--` to the script; the tool ignores it, so do we.
  return { script, args: rest.filter((a) => a !== "--") };
}

export function commandFor(script: string, args: string[]): string {
  return ["pnpm --filter", PACKAGE, script, ...args].join(" ");
}

const now = () => new Date().toISOString();
const stamp = () => now().replace(/[:.]/g, "-");
const has = (args: string[], f: string) => args.includes(f);

export async function runCommand(line: string, world: SimWorld, io: SimIo): Promise<number> {
  let parsed: { script: string; args: string[] };
  try { parsed = parseCommand(line); } catch (e) { io.print(`[fatal] ${(e as Error).message}`, "error"); return 1; }
  const { script, args } = parsed;
  if (!script || script === "help") { help(io); return 0; }
  const def = SCRIPTS[script];
  if (!def) {
    io.print(`ERR_PNPM_NO_SCRIPT  Missing script: ${script}`, "error");
    io.print(`Scripts: ${Object.keys(SCRIPTS).join(", ")}  ·  type help`, "muted");
    return 1;
  }
  io.print(`> ${PACKAGE}@0.1.0 ${script}`, "muted");
  io.print(`> tsx ${def.file}${args.length ? ` ${args.join(" ")}` : ""}`, "muted");
  io.print("");
  try {
    switch (script) {
      case "migrate:team":    await runTeam(args, world, io); break;
      case "preview":         await runTeam(["--preview", ...args], world, io); break;
      case "migrate:modules": await runModules(args, world, io); break;
      case "verify":          await verify(args, world, io); break;
      case "migrate:all":     return await runAll(args, world, io);
      case "status":          progressLines(world.memory, true).forEach((l) => io.print(l, tone(l))); break;
      case "history":
        historyLines(world.memory).forEach((l) => io.print(l, tone(l)));
        progressLines(world.memory).forEach((l) => io.print(l, tone(l)));
        break;
      case "show":            show(args, world, io); break;
      case "reset":           await resetMemory(args, world, io); break;
      case "reset:db":        await resetDb(world, io); break;
      case "docs:organize":
        io.print("[docs:organize] Sorts files downloaded from the legacy site into <folder>/<empCode>/…", "info");
        io.print("  Not simulated: it reads real documents. Its output folder is what --docs-dir points at.", "muted");
        break;
      case "docs:import":     await docsImport(args, world, io); break;
    }
    return 0;
  } catch (e) {
    io.progress(null);
    io.print("");
    io.print(`[fatal] ${e instanceof Fatal ? "" : "Error: "}${(e as Error).message}`, "error");
    io.commit();
    return 1;
  }
}

function tone(line: string): LineTone {
  if (line.startsWith("──") || line.includes("── ")) return "head";
  return "plain";
}

function help(io: SimIo) {
  io.print("Migration v2 simulator: the tool's commands, run on synthetic employees. Nothing reaches HRMS.", "info");
  io.print("Type a command with or without the pnpm prefix, e.g.", "muted");
  io.print("  pnpm --filter @hrms/migration-v2 preview --status=ACTIVE --percent=25", "plain");
  io.print("  migrate:team --dry-run --department=HR --with-managers", "plain");
  io.print("");
  for (const [name, s] of Object.entries(SCRIPTS)) io.print(`  ${name.padEnd(16)} ${s.about}`, "plain");
  io.print("");
  io.print("  clear            empty this console      ↑ / ↓  previous commands", "muted");
}

// ── migrate:team ─────────────────────────────────────────────────────────────

async function runTeam(args: string[], world: SimWorld, io: SimIo) {
  const flag = (f: string) => has(args, f);
  const dryRun = flag("--dry-run");
  const doReset = flag("--reset");
  const docsDir = arg(args, "--docs-dir");
  const hrPath = arg(args, "--hr-report");
  const preview = flag("--preview");
  if (!hrPath && !dryRun && !preview && !flag("--no-hr-report")) {
    throw new Error("Pass --hr-report <Active Employee Report.xlsx> (or --no-hr-report to load legacy roles as they are)");
  }
  const state = world.memory;

  io.print("╔══════════════════════════════════════════════════╗", "head");
  io.print("║   HRMS Migration v2 — team.integertel.com          ║", "head");
  io.print("╚══════════════════════════════════════════════════╝", "head");
  if (dryRun) io.print("  *** DRY RUN — no writes ***", "warn");
  if (preview) io.print("  *** PREVIEW — selection only, no writes ***", "info");
  progressLines(state).forEach((l) => io.print(l, tone(l)));
  let run: ReturnType<typeof startRun> | null = null;

  try {
    if (doReset && !dryRun && !preview) {
      io.print("");
      io.print("[reset] Truncating employees, users, departments, offices (CASCADE)…", "warn");
      await io.sleep(400);
      world.hrms = { ...emptyHrms(), holidays: world.hrms.holidays };
      const archived = resetState(world);
      if (archived) io.print(`[reset] Run memory archived → ${archived}`, "warn");
      io.print("[reset] Done.");
    } else if (dryRun) {
      io.print(""); io.print("[reset] Skipped (dry-run)", "muted");
    } else {
      io.print(""); io.print("[reset] Skipped (pass --reset to truncate)", "muted");
    }

    io.print(""); io.print("[extract] Reading from softqlco_teami…", "info");
    await io.sleep(500);
    const mapped = generateSource(world.settings);
    const rawRows = mapped.length + Math.round(mapped.length * 0.035);
    if (hrPath) io.print(`[hr-report] ${mapped.filter((m) => m.status !== "TERMINATED").length} rows from ${hrPath}`);
    io.print(""); io.print("[map] Deduplicating by email…");
    await io.sleep(250);
    io.print(`[map] ${rawRows} rows → ${mapped.length} after email dedup`);
    io.print(""); io.print("[map] Transforming rows…");
    await io.sleep(250);
    io.print(`[map] ${mapped.length} employees ready`);

    updateSource(state, mapped);
    const migrated = migratedBayIds(state);
    // The page is a terminal: no selection flags and no --all/--yes means interactive, and the
    // filter panel holds the answers to the picker's questions.
    const interactive = flag("--interactive") || flag("-i") || (!hasSelectionFlags(args) && !flag("--all") && !flag("--yes"));
    let filters: SelectionFilters;
    if (interactive) {
      const remaining = mapped.filter((m) => !migrated.has(m.legacyBayUserId)).length;
      io.print(""); io.print("── Choose who to migrate ─────────────────────────", "head");
      io.print(`  ${mapped.length} employees in the source, ${mapped.length - remaining} already migrated, ${remaining} to go.`);
      filters = io.panelFilters();
      io.print(`  answers from the filter panel: ${describeFilters(filters)}`, "info");
    } else {
      filters = filtersFromArgs(args, (name) => world.files[name] ?? null);
    }
    validateFilters(filters, mapped);
    const sel = selectEmployees(mapped, filters, migrated);
    selectionLines(sel, filters, preview ? 1000 : 25).forEach((l) => io.print(l, tone(l)));

    if (preview) {
      io.commit();
      progressLines(state).forEach((l) => io.print(l, tone(l)));
      return;
    }
    if (sel.picked.length === 0) {
      io.print(""); io.print("[select] No one left to migrate for these filters (add --include-migrated to re-load).", "warn");
      io.commit();
      progressLines(state).forEach((l) => io.print(l, tone(l)));
      return;
    }
    if (interactive && !(await io.confirm({
      title: `Migrate these ${sel.picked.length} employees${dryRun ? " (dry run)" : ""}?`,
      description: `Filters: ${describeFilters(filters)}. ${dryRun ? "A dry run records the run but remembers no one as migrated." : "They are written to HRMS (simulated) and remembered, so the next run skips them."}`,
      actionText: dryRun ? "Dry run" : "Migrate",
    }))) {
      io.print(""); io.print("[select] Cancelled — nothing written.", "warn");
      return;
    }

    const picked = sel.picked;
    run = startRun(state, filters, sel.matched.length, picked, dryRun);
    io.commit();
    io.print(""); io.print(`[run] ${run.id} — ${picked.length} employees${dryRun ? " (dry run)" : ""}`, "info");

    const result = await loadEmployees(picked, mapped, dryRun, world, io);
    io.print(""); io.print("── Employee Migration Result ──────────────────────────────────", "head");
    io.print(`  officesUpserted: ${result.officesUpserted}`);
    io.print(`  departmentsUpserted: ${result.departmentsUpserted}`);
    io.print(`  employeesUpserted: ${result.employeesUpserted}`);
    io.print(`  usersUpserted: ${result.usersUpserted}`);
    io.print(`  managersResolved: ${result.managersResolved}`);
    io.print(`  managersPending: ${result.managersPending}`);
    io.print(`  skipped: 0`);
    io.print(`  errors: ${result.errors.length}`, result.errors.length ? "error" : "plain");
    if (result.errors.length > 0 && result.errors.length <= 5) result.errors.forEach((e) => io.print(`     { source: 'bay_users', id: ${e.id}, email: '${e.email}', error: '${e.error}' }`, "error"));
    if (result.errors.length > 0) { io.print(""); io.print(`[warn] ${result.errors.length} employee rows failed`, "warn"); }
    finishRun(state, run, picked, result.errors);
    io.commit();

    if (docsDir) {
      io.print(""); io.print(`[docs] Loading legacy documents from ${docsDir} for this run's employees…`, "info");
      const ok = picked.filter((m) => !m.failsToLoad);
      const d = importDocs(ok, dryRun, world);
      await io.sleep(300);
      printDocs(d, io);
    } else {
      io.print(""); io.print("[docs] Skipped (pass --docs-dir <folder from docs:organize>)", "muted");
    }

    io.print(""); io.print(`[report] → tools/migration-v2/reports/migration-${stamp()}.json`, "muted");
    progressLines(state, true).forEach((l) => io.print(l, tone(l)));
    io.print(""); io.print(`[done] ${run.id} complete ✓  (next: migrate:modules --batch=${run.id}, then verify --batch=${run.id})`, "success");
  } catch (err) {
    if (run && run.outcome === "running") {
      run.outcome = "crashed";
      run.finishedAt = now();
      io.commit();
    }
    throw err;
  }
}

async function loadEmployees(picked: SimEmployee[], all: SimEmployee[], dryRun: boolean, world: SimWorld, io: SimIo) {
  const errors: Array<{ id: number; email: string; error: string }> = [];
  io.print(""); io.print("[load-team] Phase 1: Resolve offices");
  await io.sleep(200);
  io.print("[load-team] Phase 2: Upsert departments");
  await io.sleep(200);
  io.print(`[load-team] Phase 3: Upsert ${picked.length} employees + users${dryRun ? " (dry run)" : ""}`);
  const step = Math.max(1, Math.ceil(picked.length / 40));
  for (let i = 0; i < picked.length; i++) {
    const m = picked[i]!;
    if (m.failsToLoad) errors.push({ id: m.legacyBayUserId, email: m.email, error: m.failsToLoad });
    else if (!dryRun) world.hrms.employees[String(m.legacyBayUserId)] = { code: m.employeeCode, managerSet: false };
    if (i % step === 0 || i === picked.length - 1) {
      io.progress({ label: `Loading employees ${i + 1}/${picked.length}`, percent: Math.round(((i + 1) / picked.length) * 100) });
      await io.sleep(Math.min(60, 2400 / picked.length) * step);
    }
  }
  io.progress(null);

  // Managers: anyone in HRMS (this run or an earlier one) can be someone's manager now; people
  // from earlier runs whose manager arrives in this run get it set too.
  io.print("[load-team] Phase 4: Resolve reporting managers");
  await io.sleep(200);
  const inHrms = (id: number | null) => id !== null && (dryRun ? picked.some((p) => p.legacyBayUserId === id && !p.failsToLoad) || !!world.hrms.employees[String(id)] : !!world.hrms.employees[String(id)]);
  const byBay = new Map(all.map((m) => [m.legacyBayUserId, m]));
  let resolved = 0, pending = 0;
  const loadedNow = picked.filter((m) => !m.failsToLoad);
  for (const m of loadedNow) {
    if (m.managerBayId === null) continue;
    if (inHrms(m.managerBayId)) { resolved++; if (!dryRun) world.hrms.employees[String(m.legacyBayUserId)]!.managerSet = true; }
    else pending++;
  }
  if (!dryRun) {
    for (const [bay, e] of Object.entries(world.hrms.employees)) {
      const m = byBay.get(Number(bay));
      if (e.managerSet || !m?.managerBayId || !inHrms(m.managerBayId)) continue;
      e.managerSet = true;
      resolved++;
    }
  }
  return {
    officesUpserted: new Set(loadedNow.map((m) => m.officeId)).size,
    departmentsUpserted: new Set(loadedNow.map((m) => m.departmentName)).size,
    employeesUpserted: loadedNow.length,
    usersUpserted: loadedNow.length,
    managersResolved: resolved,
    managersPending: pending,
    errors,
  };
}

// ── documents ────────────────────────────────────────────────────────────────

function importDocs(people: SimEmployee[], dryRun: boolean, world: SimWorld) {
  let imported = 0, skipped = 0, noEmployee = 0;
  for (const m of people) {
    const key = String(m.legacyBayUserId);
    if (!world.hrms.employees[key] && !dryRun) { noEmployee += m.documents; continue; }
    const before = world.hrms.documents[key] ?? 0;
    skipped += Math.min(before, m.documents);
    imported += m.documents - Math.min(before, m.documents);
    if (!dryRun) world.hrms.documents[key] = m.documents;
  }
  return { imported, skipped, notOk: 0, noEmployee, errors: 0 };
}

function printDocs(d: ReturnType<typeof importDocs>, io: SimIo) {
  io.print(""); io.print("── Document Migration Result ──────────────────────────────────", "head");
  io.print(`  imported: ${d.imported}`); io.print(`  skipped: ${d.skipped}`); io.print(`  notOk: ${d.notOk}`);
  io.print(`  noEmployee: ${d.noEmployee}`); io.print(`  errors: ${d.errors}`);
}

async function docsImport(args: string[], world: SimWorld, io: SimIo) {
  const dir = arg(args, "--docs-dir") ?? args.find((a) => !a.startsWith("--"));
  if (!dir) throw new Error("Pass --docs-dir <folder from docs:organize>");
  const dryRun = has(args, "--dry-run");
  const all = generateSource(world.settings);
  const people = all.filter((m) => world.hrms.employees[String(m.legacyBayUserId)]);
  io.print(`[docs] Loading legacy documents from ${dir} for ${people.length} employees in HRMS…`, "info");
  await io.sleep(400);
  printDocs(importDocs(people, dryRun, world), io);
  io.commit();
}

// ── migrate:modules ──────────────────────────────────────────────────────────

const MODULES = ["personal", "termination", "leave", "attendance", "holiday", "compensation"] as const;

interface ModuleResult { module: string; read: number; written: number; skipped: number; reasons: Record<string, number>; notes: string[] }

async function runModules(args: string[], world: SimWorld, io: SimIo) {
  const dryRun = has(args, "--dry-run");
  const pickedMods = args.find((a) => a.startsWith("--module="))?.replace("--module=", "");
  const names = pickedMods ? pickedMods.split(",") : [...MODULES];
  const batchRef = args.find((a) => a.startsWith("--batch="))?.replace("--batch=", "");
  const state = world.memory;
  const run = batchRef ? findRun(state, batchRef) : null;
  if (run) io.print(`[batch] ${run.id}: ${run.bayIds.length} employees (${run.filterText})`, "info");

  const unknown = names.filter((n) => !(MODULES as readonly string[]).includes(n));
  if (unknown.length) throw new Error(`Unknown module(s): ${unknown.join(", ")}. Valid: ${MODULES.join(", ")}`);
  io.print(`HRMS team modules: ${names.join(", ")}${dryRun ? "  [DRY RUN — no writes]" : ""}`);

  const loaded = Object.keys(world.hrms.employees).length;
  if (loaded === 0) throw new Error("No employees with legacyBayUserId — run `pnpm migrate:team` first.");
  io.print(`[check] ${loaded} migrated employees available for joins`);

  const all = generateSource(world.settings);
  const scope = run ? new Set(run.bayIds) : null;
  const rows = all.filter((m) => !scope || scope.has(m.legacyBayUserId));
  const inHrms = (m: SimEmployee) => !!world.hrms.employees[String(m.legacyBayUserId)];
  const results: ModuleResult[] = [];

  for (const [i, name] of names.entries()) {
    io.print(""); io.print(`── ${name} ──`, "head");
    io.progress({ label: `Module ${name} (${i + 1}/${names.length})`, percent: Math.round(((i + 0.5) / names.length) * 100) });
    await io.sleep(350);
    const r: ModuleResult = { module: name, read: 0, written: 0, skipped: 0, reasons: {}, notes: [] };
    const skip = (why: string, n = 1) => { if (n <= 0) return; r.skipped += n; r.reasons[why] = (r.reasons[why] ?? 0) + n; };
    const write = (store: Record<string, true>, m: SimEmployee) => { r.written++; if (!dryRun) store[String(m.legacyBayUserId)] = true; };

    if (name === "personal") {
      for (const m of rows) { r.read++; if (inHrms(m)) write(world.hrms.personal, m); else skip("employee not migrated"); }
    } else if (name === "termination") {
      for (const m of rows.filter((x) => x.status === "TERMINATED")) { r.read++; if (inHrms(m)) write(world.hrms.terminations, m); else skip("employee not migrated"); }
    } else if (name === "compensation") {
      for (const m of rows.filter((x) => x.status !== "TERMINATED" && x.employeeType !== "CONTRACTOR")) { r.read++; if (inHrms(m)) write(world.hrms.compensation, m); else skip("employee not migrated"); }
    } else if (name === "leave") {
      let noApprover = 0;
      for (const m of rows) {
        r.read += m.leaves;
        if (!inHrms(m)) { skip("employee not migrated", m.leaves); continue; }
        skip(m.leaves % 2 ? "blank leave type" : "zero/invalid days", m.badLeaves);
        const good = m.leaves - m.badLeaves;
        r.written += good;
        if (!dryRun) world.hrms.leaves[String(m.legacyBayUserId)] = good;
        if (good && m.managerBayId && !world.hrms.employees[String(m.managerBayId)]) noApprover += good;
      }
      if (noApprover) r.notes.push(`${noApprover} leaves have no approver yet: their approver is not migrated (set when a later batch loads them)`);
    } else if (name === "attendance") {
      for (const m of rows) {
        r.read += m.attendanceDays;
        if (!inHrms(m)) { skip("employee not migrated", m.attendanceDays); continue; }
        r.written += m.attendanceDays;
        if (!dryRun) world.hrms.attendance[String(m.legacyBayUserId)] = m.attendanceDays;
      }
    } else if (name === "holiday") {
      r.read = HOLIDAY_ROWS; r.written = HOLIDAY_ROWS;
      if (!dryRun) world.hrms.holidays = HOLIDAY_ROWS;
      r.notes.push("company-wide: not limited to the batch");
    }
    results.push(r);
    io.print(`  read=${r.read} written=${r.written} skipped=${r.skipped}`);
    for (const [why, n] of Object.entries(r.reasons).sort((a, b) => b[1] - a[1])) io.print(`    skipped ${n} × ${why}`, "muted");
    r.notes.forEach((n) => io.print(`  · ${n}`, "muted"));
  }
  io.progress(null);

  if (run) {
    const at = now();
    run.modules = [
      ...(run.modules ?? []).filter((m) => !names.includes(m.module) || m.dryRun !== dryRun),
      ...results.map((r): ModuleSummary => ({ module: r.module, read: r.read, written: r.written, skipped: r.skipped, dryRun, at })),
    ];
  }
  io.commit();

  io.print(""); io.print("── Reconciliation ──", "head");
  for (const r of results) io.print(`  ${r.module.padEnd(12)} source=${r.read}  written=${r.written}  skipped=${r.skipped}`);
}

// ── verify ───────────────────────────────────────────────────────────────────

async function verify(args: string[], world: SimWorld, io: SimIo) {
  const batchRef = args.find((a) => a.startsWith("--batch="))?.replace("--batch=", "");
  const state = world.memory;
  const run = batchRef ? findRun(state, batchRef) : null;
  const scope = run ? new Set(run.bayIds) : null;
  if (run) io.print(`[batch] ${run.id}: ${run.bayIds.length} employees (${run.filterText})`, "info");
  io.progress({ label: "Reconciling leave and attendance", percent: 40 });
  await io.sleep(500);

  const all = generateSource(world.settings);
  const src = all.filter((m) => !scope || scope.has(m.legacyBayUserId));
  const emps = src.filter((m) => world.hrms.employees[String(m.legacyBayUserId)]);
  const employees = scope ? {
    picked: scope.size,
    inHrms: emps.length,
    missing: [...scope].filter((id) => !world.hrms.employees[String(id)]),
    withoutLogin: [] as string[],
  } : undefined;

  const reasons: Record<string, number> = {};
  const leaveRows: unknown[] = [];
  const attRows: unknown[] = [];
  let leaveMatch = 0, leaveMismatch = 0, attMatch = 0, attMismatch = 0, noData = 0;
  for (const m of emps) {
    if (m.leaves === 0 && m.attendanceDays === 0) { noData++; continue; }
    const key = String(m.legacyBayUserId);
    const newCount = world.hrms.leaves[key] ?? 0;
    const missing = m.leaves - newCount;
    const explained = Math.min(missing, m.badLeaves);
    if (explained) reasons[m.leaves % 2 ? "blank leave type" : "zero/invalid days"] = (reasons[m.leaves % 2 ? "blank leave type" : "zero/invalid days"] ?? 0) + explained;
    if (missing - explained > 0) reasons.other = (reasons.other ?? 0) + missing - explained;
    if (missing === explained) leaveMatch++;
    else { leaveMismatch++; leaveRows.push({ code: m.employeeCode, old: m.leaves, new: newCount, missing, unexplained: missing - explained }); }
    const newDays = world.hrms.attendance[key] ?? 0;
    if (newDays === m.attendanceDays) attMatch++;
    else { attMismatch++; attRows.push({ code: m.employeeCode, oldDays: m.attendanceDays, newDays, diff: newDays - m.attendanceDays }); }
  }
  const summary: VerifySummary = {
    scope: run ? run.id : "all migrated employees",
    ...(employees ? { employees } : {}),
    employeesChecked: emps.length, withNoOldLeaveOrAttendance: noData,
    leave: { match: leaveMatch, mismatch: leaveMismatch, skippedRowsByReason: reasons, oldUsersNotMigrated: src.filter((m) => m.leaves > 0 && !world.hrms.employees[String(m.legacyBayUserId)]).length },
    attendance: { match: attMatch, mismatch: attMismatch },
    totals: {
      oldLeaves: src.reduce((s, m) => s + m.leaves, 0),
      newLeaves: emps.reduce((s, m) => s + (world.hrms.leaves[String(m.legacyBayUserId)] ?? 0), 0),
      oldAttendanceDays: src.reduce((s, m) => s + m.attendanceDays, 0),
      newAttendanceDays: emps.reduce((s, m) => s + (world.hrms.attendance[String(m.legacyBayUserId)] ?? 0), 0),
    },
  };
  io.progress(null);
  const ok = !leaveMismatch && !attMismatch && !(employees?.missing.length);
  JSON.stringify(summary, null, 2).split("\n").forEach((l) => io.print(l, ok ? "success" : "plain"));
  io.print(""); io.print(`leave mismatches (first 10): ${JSON.stringify(leaveRows.slice(0, 10))}`, leaveRows.length ? "warn" : "muted");
  io.print(`attendance mismatches (first 10): ${JSON.stringify(attRows.slice(0, 10))}`, attRows.length ? "warn" : "muted");
  io.print(""); io.print(`report → tools/migration-v2/reports/verify-${stamp()}.json`, "muted");

  const at = now();
  if (run) run.verify = { at, summary };
  else state.verifications.push({ at, scope: "all", summary });
  io.commit();
}

// ── migrate:all ──────────────────────────────────────────────────────────────

async function runAll(args: string[], world: SimWorld, io: SimIo): Promise<number> {
  const dryRun = has(args, "--dry-run");
  const step = async (label: string, fn: () => Promise<void> | void): Promise<boolean> => {
    io.print(""); io.print(`████ ${label}`, "head");
    try { await fn(); return true; } catch (e) {
      io.progress(null);
      io.print(`[fatal] Error: ${(e as Error).message}`, "error");
      io.print(""); io.print(`[migrate:all] ${label.split(" ")[0]} failed (exit 1) — later steps not run`, "error");
      io.commit();
      return false;
    }
  };
  const runsBefore = world.memory.runs.length;
  if (!(await step(`run-team.ts ${args.join(" ")}`, () => runTeam(args, world, io)))) return 1;
  if (world.memory.runs.length === runsBefore) {
    io.print(""); io.print("[migrate:all] no run recorded (preview, cancelled or no one picked) — modules and verify skipped", "warn");
    return 0;
  }
  const run = world.memory.runs[world.memory.runs.length - 1]!;
  const modArgs = [`--batch=${run.id}`, ...(dryRun ? ["--dry-run"] : [])];
  if (!(await step(`run-modules.ts ${modArgs.join(" ")}`, () => runModules(modArgs, world, io)))) return 1;
  if (!dryRun && !(await step(`verify-modules.ts --batch=${run.id}`, () => verify([`--batch=${run.id}`], world, io)))) return 1;
  await step("progress.ts status", () => progressLines(world.memory, true).forEach((l) => io.print(l, tone(l))));
  io.print(""); io.print(`[migrate:all] ${run.id}: all steps finished`, "success");
  return 0;
}

// ── show / reset ─────────────────────────────────────────────────────────────

function show(args: string[], world: SimWorld, io: SimIo) {
  const state = world.memory;
  const run = findRun(state, args.find((a) => !a.startsWith("--")) ?? "last");
  io.print(`${run.id}  ${run.dryRun ? "DRY" : "REAL"}  ${run.outcome}  filters: ${run.filterText}`, "head");
  const failed = new Map(run.failed.map((f) => [f.bayId, f]));
  for (const bayId of run.bayIds) {
    const e = state.employees[String(bayId)];
    const f = failed.get(bayId);
    const label = f ? `FAILED ${f.code}: ${f.error.slice(0, 120)}`
      : e && e.runId === run.id ? `${e.code.padEnd(10)} ${e.name.padEnd(28)} ${e.status.padEnd(13)} ${e.department}`
      : run.dryRun ? "(dry run)" : "(loaded again by a later run)";
    io.print(`  bay ${String(bayId).padEnd(6)} ${label}`, f ? "error" : "plain");
  }
  if (run.modules?.length) {
    io.print("");
    run.modules.forEach((m, i) => io.print(`  ${i ? "         " : "modules: "}${m.module}${m.dryRun ? "(dry)" : ""} read=${m.read} written=${m.written} skipped=${m.skipped}`));
  }
  if (run.verify) {
    io.print(""); io.print(`  verify ${run.verify.at}:`);
    JSON.stringify(run.verify.summary, null, 2).split("\n").forEach((l) => io.print(l, "muted"));
  }
}

async function resetMemory(args: string[], world: SimWorld, io: SimIo) {
  const state = world.memory;
  if (!state.runs.length && !Object.keys(state.employees).length) {
    io.print("Nothing to reset — no runs recorded yet.");
    return;
  }
  progressLines(state).forEach((l) => io.print(l, tone(l)));
  const yes = has(args, "--yes") || await io.confirm({
    title: "Reset the run memory?",
    description: `Forget ${state.runs.length} runs and ${Object.keys(state.employees).length} migrated employees, and start from 0%? HRMS data is not touched.`,
    actionText: "Reset memory",
    destructive: true,
  });
  if (!yes) { io.print("Cancelled.", "warn"); return; }
  const archived = resetState(world);
  io.commit();
  io.print(`Run memory reset. Previous memory archived → ${archived}`, "success");
  io.print(`Next migrate:team run starts from 0% (${STATE_FILE}).`);
}

async function resetDb(world: SimWorld, io: SimIo) {
  io.print("[reset] Truncating employees, users, departments, offices (CASCADE)…", "warn");
  await io.sleep(500);
  world.hrms = { ...emptyHrms(), holidays: world.hrms.holidays };
  io.commit();
  io.print("[reset] Done — DB is clean for fresh run.", "success");
  if (Object.keys(world.memory.employees).length) {
    io.print(`  note: the run memory still lists ${progress(world.memory).migrated} employees as migrated; run reset too, or re-load them with --include-migrated.`, "muted");
  }
}
